import { type RefObject, useEffect, useState } from "react";

type UseInViewportOptions = {
  /**
   * Distance around the viewport that still counts as "in view". A positive
   * margin pre-loads images just before they scroll in, hiding latency.
   */
  rootMargin?: string;
  /**
   * Skip observation entirely and report `true` immediately — used for
   * above-the-fold / LCP images that must start loading on first paint.
   */
  disabled?: boolean;
};

/**
 * Reports whether `ref`'s element has entered (or is near) the viewport, using
 * a single one-shot IntersectionObserver that disconnects on first hit. This
 * keeps offscreen images out of the network/memory budget until they matter.
 *
 * Degrades gracefully where IntersectionObserver is unavailable (older engines,
 * jsdom) by reporting `true`, so images always render.
 */
export function useInViewport<T extends Element>(
  ref: RefObject<T>,
  { rootMargin = "300px", disabled = false }: UseInViewportOptions = {},
): boolean {
  const [inView, setInView] = useState(disabled);

  useEffect(() => {
    if (disabled || inView) return;

    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, rootMargin, disabled, inView]);

  return inView;
}
